import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/utils';

const toastVariants = cva(
  'pointer-events-auto flex w-full max-w-md items-start gap-3 rounded-lg border p-4 shadow-theme-lg transition-all',
  {
    variants: {
      variant: {
        default: 'border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900',
        success: 'border-success-200 bg-success-50 dark:border-success-800 dark:bg-success-900/30',
        error: 'border-error-200 bg-error-50 dark:border-error-800 dark:bg-error-900/30',
        warning: 'border-warning-200 bg-warning-50 dark:border-warning-800 dark:bg-warning-900/30',
        info: 'border-blue-light-200 bg-blue-light-50 dark:border-blue-light-800 dark:bg-blue-light-900/30',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export interface ToastProps extends VariantProps<typeof toastVariants> {
  title?: string;
  description?: string;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  onClose?: () => void;
  className?: string;
}

export const Toast: React.FC<ToastProps> = ({
  variant,
  title,
  description,
  icon,
  action,
  onClose,
  className,
}) => {
  return (
    <div className={cn(toastVariants({ variant }), className)} role="alert">
      {icon && <div className="flex-shrink-0">{icon}</div>}
      <div className="flex-1">
        {title && (
          <p className="font-semibold text-gray-900 dark:text-white">{title}</p>
        )}
        {description && (
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{description}</p>
        )}
        {action && <div className="mt-3">{action}</div>}
      </div>
      {onClose && (
        <button
          onClick={onClose}
          className="flex-shrink-0 text-gray-400 transition-colors hover:text-gray-600 dark:hover:text-gray-300"
          aria-label="Close"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
};

interface ToastContextValue {
  toasts: Array<{ id: string; props: ToastProps }>;
  addToast: (props: ToastProps) => string;
  removeToast: (id: string) => void;
}

const ToastContext = React.createContext<ToastContextValue | undefined>(undefined);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = React.useState<Array<{ id: string; props: ToastProps }>>([]);

  const addToast = (props: ToastProps) => {
    const id = Math.random().toString(36).substring(7);
    setToasts((prev) => [...prev, { id, props }]);
    return id;
  };

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      {children}
      <div className="pointer-events-none fixed bottom-0 right-0 z-50 flex max-h-screen w-full flex-col-reverse gap-2 p-4 sm:max-w-md">
        {toasts.map(({ id, props }) => (
          <Toast
            key={id}
            {...props}
            onClose={() => {
              props.onClose?.();
              removeToast(id);
            }}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useToast = () => {
  const context = React.useContext(ToastContext);
  if (!context) throw new Error('useToast must be used within ToastProvider');
  return context;
};
